import { Injectable } from '@angular/core';
import { ISelf, ITag } from '@shared/models/api/base';
import { map, Observable } from 'rxjs';
import { CrudService } from './crud.service';

@Injectable({
	providedIn: 'root',
})
export class PrintService {
	private baseEndpoint = '/print';
	constructor(private crudService: CrudService) {}

	public getPrinters(): Observable<IPrinter[]> {
		return this.crudService.get<{ items: IPrinter[] }>(`${this.baseEndpoint}/printers`).pipe(
			map((response) =>
				response.items.map((printer) => ({
					...printer,
				})),
			),
		);
	}

	public getPrinterById(printerId: string): Observable<IPrinter> {
		return this.crudService.get<IPrinter>(`${this.baseEndpoint}/printers/${printerId}`).pipe(map((printer) => ({ ...printer })));
	}

	public getPrinterByAlias(alias: string): Observable<IPrinter> {
		return this.crudService.get<IPrinter>(`${this.baseEndpoint}/printers/alias/${encodeURIComponent(alias)}`).pipe(map((printer) => ({ ...printer })));
	}

	public deletePrinter(printer: IPrinter): Observable<object> {
		return this.crudService.delete(printer);
	}

	public createPrinter(printer: IPrinterCreate): Observable<IPrinter> {
		return this.crudService.create<IPrinterCreate, IPrinter>(printer, `${this.baseEndpoint}/printers`);
	}

	public updatePrinter(printer: IPrinter): Observable<IPrinter> {
		return this.crudService.update<IPrinter, IPrinter>(printer, printer.self);
	}

	public printerSelfRef(printerId: string): string {
		return `${this.baseEndpoint}/printers/${printerId}`;
	}

	public templateSelfRef(templateId: string): string {
		return `/docs/templates/${templateId}`;
	}

	public print(printer: string, urls: string[]): Observable<void> {
		return this.crudService.create<IPrintJobCollectionRequest, void>({ jobs: [{ printer, urls }] }, `${this.baseEndpoint}/collections`);
	}

	public printWithDoc<T>(param: T, templateId: string, printer: string): Observable<void> {
		return this.crudService.create<IPrintJobCollectionRequest, void>({ jobs: [{ printer, template: templateId }], data: JSON.stringify(param) }, `${this.baseEndpoint}/collections`);
	}

	public getCollections(limit?: number, nextToken?: string): Observable<{ items: IPrintJobCollection[]; nextToken?: string }> {
		const query = [limit ? `limit=${limit}` : undefined, nextToken ? `nextToken=${encodeURIComponent(nextToken)}` : undefined].filter(Boolean).join('&');
		const path = `${this.baseEndpoint}/collections${query ? `?${query}` : ''}`;

		return this.crudService.get<{ items: IPrintJobCollection[]; page?: { nextToken?: string | null } }>(path).pipe(
			map((response) => ({
				items: response.items,
				nextToken: response.page?.nextToken ?? undefined,
			})),
		);
	}

	public getPrintJobs(status?: PrintJobStatus, limit?: number, nextToken?: string): Observable<{ items: IPrintJobItem[]; nextToken?: string }> {
		const query = [status ? `status=${status}` : undefined, limit ? `limit=${limit}` : undefined, nextToken ? `nextToken=${encodeURIComponent(nextToken)}` : undefined].filter(Boolean).join('&');
		const path = `${this.baseEndpoint}/printjobs${query ? `?${query}` : ''}`;

		return this.crudService.get<{ items: IPrintJobItem[]; page?: { nextToken?: string | null } }>(path).pipe(
			map((response) => ({
				items: response.items,
				nextToken: response.page?.nextToken ?? undefined,
			})),
		);
	}
}
interface IPrinterBase {
	alias: string;
	description: string;
	encoding: string;
	printerPath: string;
	tags: ITag[];
}

export interface IPrinterCreate extends IPrinterBase {}

export interface IPrinter extends ISelf, IPrinterBase {}

interface IPrintJobRequest {
	printer: string;
	template?: string;
	urls?: string[];
}

interface IPrintJobCollectionRequest {
	jobs: IPrintJobRequest[];
	data?: string;
}

export type PrintJobStatus = 'Pending' | 'Processing' | 'Failed' | 'Succeeded';

export interface IPrintJob {
	printer: string;
	template?: string;
	status: PrintJobStatus;
	urls?: string[];
	errorMessage?: string;
}

export interface IPrintJobCollection extends ISelf {
	jobs: IPrintJob[];
	data?: string;
	meta: { createdAt: number };
}

// The individual-job endpoint's own response shape - a superset of the embedded IPrintJob
// (which collections carry inline) with its own self ref, parent collection ref, and timestamps.
export interface IPrintJobItem extends IPrintJob, ISelf {
	printJobCollectionSelf: string;
	meta: { createdAt: number };
}

// Collapsed 3-value display taxonomy for a collection (the collection itself carries no
// status - only its jobs do). Precedence: any Failed job wins over any still-Working job,
// so a partial failure inside a batch is never hidden behind other jobs that succeeded.
export type CollectionDisplayStatus = 'Working' | 'Done' | 'Failed';

export function getCollectionDisplayStatus(collection: IPrintJobCollection): CollectionDisplayStatus {
	if (collection.jobs.some((job) => job.status === 'Failed')) {
		return 'Failed';
	}
	if (collection.jobs.some((job) => job.status === 'Pending' || job.status === 'Processing')) {
		return 'Working';
	}
	return 'Done';
}

export interface ICollectionJobCounts {
	done: number;
	working: number;
	failed: number;
	total: number;
}

export function getCollectionJobCounts(collection: IPrintJobCollection): ICollectionJobCounts {
	return {
		done: collection.jobs.filter((job) => job.status === 'Succeeded').length,
		working: collection.jobs.filter((job) => job.status === 'Pending' || job.status === 'Processing').length,
		failed: collection.jobs.filter((job) => job.status === 'Failed').length,
		total: collection.jobs.length,
	};
}

const PRINT_ERROR_MAX_LENGTH = 150;

// Trims a job's error message down to a table-cell-friendly length; callers pair this with a
// matTooltip bound to the untruncated message so the full text is still one hover away.
// errorMessage is optional on IPrintJob, so this accepts null/undefined directly rather than
// pushing the empty-check onto every call site (matches truncateSyncError's signature).
export function truncatePrintError(value: string | null | undefined, maxLength: number = PRINT_ERROR_MAX_LENGTH): string {
	if (!value) {
		return '';
	}
	return value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;
}
